import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save, ArrowLeft, Globe, Monitor, Tablet, Smartphone, Eye, Code2, ExternalLink,
  Settings as SettingsIcon, History, MoreVertical, Copy, Download, Upload, Trash2,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap, downloadFile } from '@/lib/api';
import { config } from '@/lib/config';
import { pickLocalized, type Localized } from '@/lib/i18n';
import { usePageEditor, type SeoFields, type OpenGraphFields } from '@/features/page-builder/usePageEditor';
import { PageBuilder } from '@/features/page-builder/PageBuilder';
import { PageSettingsPanel } from '@/features/page-builder/PageSettingsPanel';
import { RevisionsDialog } from '@/features/page-builder/RevisionsDialog';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/cn';
import type { BlockInstance } from '@sytel/blocks';

interface PagePayload {
  id: string;
  slug: string;
  locale: string;
  title: Localized;
  description: Localized;
  status: 'draft' | 'published' | 'archived' | 'scheduled';
  scheduled_at: string | null;
  blocks: BlockInstance[];
  seo: SeoFields | null;
  open_graph: OpenGraphFields | null;
  schema_data: Record<string, unknown> | Array<Record<string, unknown>> | null;
  canonical_url: string | null;
  preview_image: string | null;
  no_index: boolean | null;
  no_follow: boolean | null;
}

export const Route = createFileRoute('/_app/pages/$id')({
  component: PageEditorView,
});

type ViewMode = 'edit' | 'settings' | 'preview';
type Viewport = 'desktop' | 'tablet' | 'mobile';

const FRONTEND_URL = config.frontendUrl;

function PageEditorView() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editor = usePageEditor();
  const initialized = useRef(false);
  const [mode, setMode] = useState<ViewMode>('edit');
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [revisionsOpen, setRevisionsOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['page', id],
    queryFn: () => unwrap<PagePayload>(api.get(`/pages/${id}`)),
    enabled: id !== 'new',
  });

  useEffect(() => {
    if (initialized.current) return;
    if (id === 'new') {
      editor.init({
        pageId: null,
        title: 'Nueva página',
        slug: 'nueva-pagina',
        locale: 'es',
        status: 'draft',
        blocks: [],
      });
      initialized.current = true;
    } else if (data) {
      editor.init({
        pageId: data.id,
        title: pickLocalized(data.title, data.locale, data.slug),
        slug: data.slug,
        locale: data.locale,
        status: data.status,
        blocks: data.blocks,
        seo: data.seo ?? {},
        openGraph: data.open_graph ?? {},
        advanced: {
          scheduled_at: data.scheduled_at ? data.scheduled_at.slice(0, 16) : null,
          canonical_url: data.canonical_url,
          preview_image: data.preview_image,
          no_index: Boolean(data.no_index),
          no_follow: Boolean(data.no_follow),
          schema_data: data.schema_data,
          description: pickLocalized(data.description, data.locale, '') || null,
        },
      });
      initialized.current = true;
    }
  }, [id, data, editor]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: editor.title,
        slug: editor.slug,
        locale: editor.locale,
        status: editor.advanced.scheduled_at ? 'scheduled' : editor.status,
        description: editor.advanced.description,
        seo: cleanEmpty(editor.seo as Record<string, unknown>),
        open_graph: cleanEmpty(editor.openGraph as Record<string, unknown>),
        schema_data: editor.advanced.schema_data,
        canonical_url: editor.advanced.canonical_url,
        preview_image: editor.advanced.preview_image,
        no_index: editor.advanced.no_index,
        no_follow: editor.advanced.no_follow,
        scheduled_at: editor.advanced.scheduled_at,
        blocks: editor.blocks.map((b, i) => ({
          type: b.type,
          order: i,
          visible: b.visible,
          data: b.data,
        })),
      };
      if (editor.pageId) {
        return unwrap<PagePayload>(api.put(`/pages/${editor.pageId}`, body));
      }
      return unwrap<PagePayload>(api.post('/pages', body));
    },
    onSuccess: (saved) => {
      toast.success('Página guardada');
      editor.clearDirty();
      void qc.invalidateQueries({ queryKey: ['page-revisions', editor.pageId] });
      if (!editor.pageId) {
        void navigate({ to: '/pages/$id', params: { id: saved.id } });
      }
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Error al guardar';
      toast.error(message);
    },
  });

  const duplicate = useMutation({
    mutationFn: () => unwrap<PagePayload>(api.post(`/pages/${editor.pageId}/duplicate`)),
    onSuccess: (newPage) => {
      toast.success('Página duplicada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
      void navigate({ to: '/pages/$id', params: { id: newPage.id } });
    },
    onError: () => toast.error('No se pudo duplicar'),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/pages/${editor.pageId}`),
    onSuccess: () => {
      toast.success('Página eliminada');
      void qc.invalidateQueries({ queryKey: ['pages'] });
      void navigate({ to: '/pages' });
    },
  });

  async function exportPage() {
    if (!editor.pageId) return;
    try {
      await downloadFile(`/pages/${editor.pageId}/export`, `page-${editor.slug}.json`);
    } catch {
      toast.error('No se pudo exportar');
    }
  }

  if (id !== 'new' && isLoading) {
    return <div className="p-8 text-sm text-neutral-500">Cargando…</div>;
  }

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => void navigate({ to: '/pages' })}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
            aria-label="Volver"
          >
            <ArrowLeft className="size-4" />
          </button>
          <input
            value={editor.title}
            onChange={(e) => editor.setMeta({ title: e.target.value })}
            className="bg-transparent text-lg font-semibold outline-none"
          />
          <span className="text-sm text-neutral-400">/</span>
          <input
            value={editor.slug}
            onChange={(e) => editor.setMeta({ slug: e.target.value })}
            className="bg-transparent text-sm text-neutral-500 outline-none"
          />
          {editor.status === 'scheduled' && editor.advanced.scheduled_at && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
              <Clock className="size-3" />
              {new Date(editor.advanced.scheduled_at).toLocaleString('es')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editor.isDirty && (
            <span className="text-xs text-amber-600">Cambios sin guardar</span>
          )}
          <div className="flex items-center rounded-md border border-neutral-200 bg-neutral-50">
            <ModeBtn active={mode === 'edit'} onClick={() => setMode('edit')}>
              <Code2 className="size-3.5" /> Editor
            </ModeBtn>
            <ModeBtn active={mode === 'settings'} onClick={() => setMode('settings')}>
              <SettingsIcon className="size-3.5" /> Ajustes
            </ModeBtn>
            <ModeBtn active={mode === 'preview'} onClick={() => setMode('preview')}>
              <Eye className="size-3.5" /> Vista previa
            </ModeBtn>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            <Save className="size-4" /> {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button
            variant="success"
            onClick={async () => {
              if (!editor.pageId) {
                toast.warning('Guarda primero para poder publicar');
                return;
              }
              try {
                await api.post(`/pages/${editor.pageId}/publish`);
                toast.success('Página publicada');
              } catch {
                toast.error('No se pudo publicar');
              }
            }}
          >
            <Globe className="size-4" /> Publicar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-md border border-neutral-200 p-2 hover:bg-neutral-50" aria-label="Más acciones">
                <MoreVertical className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem disabled={!editor.pageId} onClick={() => setRevisionsOpen(true)}>
                <History className="mr-2 size-4" /> Historial de revisiones
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!editor.pageId} onClick={() => duplicate.mutate()}>
                <Copy className="mr-2 size-4" /> Duplicar página
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!editor.pageId} onClick={exportPage}>
                <Download className="mr-2 size-4" /> Exportar JSON
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!editor.pageId}
                className="text-red-600 focus:text-red-700"
                onClick={() => {
                  if (confirm('¿Eliminar esta página? Esta acción se puede deshacer desde la papelera.')) {
                    remove.mutate();
                  }
                }}
              >
                <Trash2 className="mr-2 size-4" /> Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {mode === 'edit' && <PageBuilder />}
      {mode === 'settings' && <PageSettingsPanel />}
      {mode === 'preview' && <PreviewMode slug={editor.slug} locale={editor.locale} viewport={viewport} setViewport={setViewport} />}

      <RevisionsDialog
        pageId={editor.pageId}
        open={revisionsOpen}
        onOpenChange={setRevisionsOpen}
        onRestore={() => {
          initialized.current = false;
          void qc.invalidateQueries({ queryKey: ['page', id] });
        }}
      />
    </div>
  );
}

function cleanEmpty<T extends Record<string, unknown>>(obj: T): T | null {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v != null && v !== '') out[k] = v;
  }
  return Object.keys(out).length > 0 ? (out as T) : null;
}

function ModeBtn({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md',
        active ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100',
      )}
    >
      {children}
    </button>
  );
}

function PreviewMode({
  slug, locale, viewport, setViewport,
}: {
  slug: string;
  locale: string;
  viewport: Viewport;
  setViewport: (v: Viewport) => void;
}) {
  const baseUrl = `${FRONTEND_URL}/${locale === 'es' ? '' : locale + '/'}${slug === 'home' ? '' : slug}`.replace(/\/$/, '') || FRONTEND_URL;

  const widths: Record<Viewport, string> = {
    desktop: '100%',
    tablet: '820px',
    mobile: '390px',
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-neutral-100">
      <div className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-2">
        <div className="text-xs text-neutral-500">
          Visualizando: <code className="rounded bg-neutral-100 px-1.5 py-0.5">{baseUrl}</code>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-neutral-200">
            <VpBtn active={viewport === 'desktop'} onClick={() => setViewport('desktop')}><Monitor className="size-3.5" /></VpBtn>
            <VpBtn active={viewport === 'tablet'} onClick={() => setViewport('tablet')}><Tablet className="size-3.5" /></VpBtn>
            <VpBtn active={viewport === 'mobile'} onClick={() => setViewport('mobile')}><Smartphone className="size-3.5" /></VpBtn>
          </div>
          <a
            href={baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-1 text-xs hover:bg-neutral-50"
          >
            <ExternalLink className="size-3" /> Abrir
          </a>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <div
          className="mx-auto h-full rounded-lg border border-neutral-200 bg-white shadow-sm transition-all"
          style={{ maxWidth: widths[viewport], width: widths[viewport] }}
        >
          <iframe
            key={baseUrl + viewport}
            src={baseUrl}
            title="Vista previa"
            className="h-full min-h-[600px] w-full rounded-lg"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            referrerPolicy="no-referrer"
          />
        </div>
      </div>
    </div>
  );
}

function VpBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'grid size-8 place-items-center transition-colors first:rounded-l-md last:rounded-r-md',
        active ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100',
      )}
    >
      {children}
    </button>
  );
}
